import { useCallback, useEffect, useMemo, useState } from 'react'
import { get } from 'lodash'
import { useMakePaymentMutation } from 'api/payment-gateway/paymentGateway'
import { useForm } from 'react-hook-form'
import { AMOUNT_TYPES, PAYMENT_METHODS } from 'settings/constants/payment'
import { MAKE_PAYMENT_MODEL } from 'components/payments/settings/methodModel'
import { getAmountDue, getInvoicesThatCanBePaid } from 'utils/payment'
import { shallowEqual, useSelector } from 'react-redux'
import { useLazyGetPaymentsDataQuery } from 'api/payments/payments'
import { formatCentsToDollars, formatDollarsToCents } from 'utils/price'
import usePaymentProvider from 'hooks/usePaymentProvider'

const { CURRENT_AMOUNT_DUE, OTHER_AMOUNT } = AMOUNT_TYPES
// Fortis's codes for an approved card sale and a pending bank debit.
const FORTIS_CHARGED_STATUS_CODES = [101, 131]

const useMakePayment = (factory, deps) => {
  // Selectors
  const businessId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessId'), shallowEqual)
  const accountId = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.accountId'), shallowEqual)
  const { isFortis } = usePaymentProvider()

  // Queries
  const [getPaymentsData, { data: paymentsData }] = useLazyGetPaymentsDataQuery()
  const [
    makePaymentMutation,
    { isLoading: isMakingPayment, isSuccess: isPaymentSuccessful, isError: hasPaymentError, data: makePaymentData },
  ] = useMakePaymentMutation()

  // Derived values from queries
  const unpaidInvoices = useMemo(() => {
    const invoices = get(paymentsData, 'unpaidInvoices', [])
    // Sort by due date ascending (oldest first)
    return [...invoices].sort((a, b) => {
      const dateA = new Date(a.dueDate)
      const dateB = new Date(b.dueDate)
      return dateA - dateB
    })
  }, [paymentsData])
  const currentAmountDue = useMemo(() => getAmountDue(unpaidInvoices), [unpaidInvoices])

  // Form state

  const formMethods = useForm({
    defaultValues: {
      ...MAKE_PAYMENT_MODEL,
    },
  })

  const { control, watch, setValue } = formMethods
  const formValues = watch()

  const amountDueOption = watch('amountDueOption')
  const otherAmount = watch('otherAmount')

  const getAmountToPayWithInvoices = useCallback(() => {
    if (amountDueOption === CURRENT_AMOUNT_DUE) {
      return {
        invoicesToPay: unpaidInvoices.map(({ invoiceNumber }) => invoiceNumber),
        amountCents: formatDollarsToCents(currentAmountDue),
      }
    }
    if (amountDueOption === OTHER_AMOUNT) {
      const customerCreditBalanceCents = get(paymentsData, 'customerCreditBalanceCents', 0)
      const amountCents = formatDollarsToCents(otherAmount || 0)
      return {
        invoicesToPay: getInvoicesThatCanBePaid(unpaidInvoices, customerCreditBalanceCents, amountCents),
        amountCents: formatDollarsToCents(otherAmount || 0),
      }
    }
    const selectedInvoice = unpaidInvoices.find(({ invoiceNumber }) => invoiceNumber === amountDueOption)
    return {
      invoicesToPay: [selectedInvoice.invoiceNumber],
      amountCents: selectedInvoice?.totalCents - selectedInvoice?.amountPaidCents,
    }
  }, [amountDueOption, currentAmountDue, otherAmount, paymentsData, unpaidInvoices])

  const amountCentsToPay = useMemo(() => getAmountToPayWithInvoices()?.amountCents || 0, [getAmountToPayWithInvoices])
  const amountToPay = useMemo(() => formatCentsToDollars(amountCentsToPay, false), [amountCentsToPay])

  const [isChargedByFortis, setIsChargedByFortis] = useState(false)

  const makePayment = (fortisEvent, paymentId) => {
    setIsChargedByFortis(FORTIS_CHARGED_STATUS_CODES.includes(Number(get(fortisEvent, 'data.status_code'))))
    const id = get(fortisEvent, 'data.id')
    const tokenId = get(fortisEvent, 'data.token_id')
    const expDate = get(fortisEvent, 'data.saved_account.exp_date')
    const cardName = get(fortisEvent, 'data.account_holder_name')
    const fortisType = get(fortisEvent, 'data.payment_method')
    const {
      cvv,
      selectedPaymentMethod,
      showOtherPayment,
      saveMethod,
      accountToken,
      expiry,
      transactionRef,
      accountType,
      cardHolderName,
      cardNickName,
      autoPayTime,
      type,
    } = formValues
    const isACH = type === PAYMENT_METHODS.ACH || fortisType === 'ach'
    const { invoicesToPay, amountCents } = getAmountToPayWithInvoices()
    let payload = {}
    if (!showOtherPayment) {
      const isSavedACH = selectedPaymentMethod.accountType === 'ECHK' || selectedPaymentMethod.accountType === 'Checking'
      payload = {
        accountId,
        accountToken: selectedPaymentMethod.accountToken,
        expiry: selectedPaymentMethod.expiry,
        savePaymentMethod: false,
        invoicesToPay,
        amountCents,
        cvv,
        cardHolderName,
        cardNickName,
        autoPayTime,
        ...(isSavedACH && { accountType: selectedPaymentMethod.accountType }),
      }
    } else {
      payload = {
        accountId,
        accountToken,
        expiry,
        savePaymentMethod: !!saveMethod,
        invoicesToPay,
        amountCents,
        transactionRef,
        cvv,
        cardHolderName,
        cardNickName,
        // autoPayTime,
        ...(isACH && { accountType }),
        ...(isFortis && {
          aphId: paymentId,
          accountToken: tokenId,
          transactionRef: id,
          expiry: expDate,
          cardHolderName: cardName,
          cardNickName: null,
        }),
      }
    }
    makePaymentMutation({ businessId, payload })
  }

  const setValues = values => {
    Object.keys(values).forEach(key => {
      setValue(key, values[key])
    })
  }

  useEffect(() => {
    if (businessId && accountId) {
      getPaymentsData({ businessId, accountId })
    }
  }, [businessId, accountId])

  return {
    formValues,
    setValue,
    setValues,
    control,
    currentAmountDue,
    unpaidInvoices,
    makePayment,
    isMakingPayment,
    isPaymentSuccessful,
    hasPaymentError,
    isChargedByFortis,
    transaction: makePaymentData,
    getAmountToPayWithInvoices,
    amountCentsToPay,
    amountToPay,
  }
}

export default useMakePayment
